import { Container } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getJobsColumns } from './jobs-columns/jobs-columns';
import { JobsFilter } from './jobs-filter';
import { mapData } from './utils';
import { List } from 'components/reusable/list';
import { AuthWithGoogleButton, isIapAuthRequired, resetIapAuthRequired } from 'security/iap';
import { useGlobalState } from 'store/reducers/reducer';
import { loadAccounts, loadJobs } from 'store/sagas/sagas';
import { useDebounce } from 'utils/hooks';
import { jobsOptions } from 'utils/jobs';
import { isQueriesSame, pushFiltersSearchPath, rawParseQuery, transformQueryTypes } from 'utils/url';
import { useIsMobile } from 'utils/window';
import './jobs.css';
const initialData = {
    pageSize: 50,
    page: 1,
    status: '',
    success: '',
    accountGroupSid: '',
    accountReviewed: '',
    from: null,
    to: null,
    sortBy: '',
    sortOrder: '',
    tableView: 1,
    respIdType: '',
    respIdState: '',
    ids: '',
    respIdCountry: '',
    lastName: '',
    firstName: '',
    is_disabled: '',
    includeResults: true,
    billableType: '',
    billable: '',
    aamvaSource: '',
    type: '',
    accountId: '',
    aml: '',
    email: '',
    phone: ''
};
export const Jobs = ({ basePath, isAdmin }) => {
    const { state, asyncDispatch } = useGlobalState();
    const navigate = useNavigate();
    const { pathname: windowPathname, search } = useLocation();
    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [jobs, setJobs] = useState({
        items: [],
        totalPages: 0,
        total: 0,
        page: 0,
        pageSize: 0
    });
    const [loading, setLoading] = useState(false);
    const parsedQuery = transformQueryTypes(mapData, rawParseQuery());
    const [filter, setFilter] = useState({
        ...initialData,
        ...parsedQuery
    });
    const debouncedFilter = useDebounce(filter, 250);
    const isMobile = useIsMobile();
    const jobsColumns = getJobsColumns(isAdmin, state.accounts, state.accountGroups);
    const jobsColumnsProps = jobsColumns
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        .filter((column) => (isMobile && column.showMobile) || !isMobile)
        .map((column) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { showMobile: _unused, ...restColumns } = column;
        return restColumns;
    });
    useEffect(() => {
        const scrollHeight = sessionStorage.getItem('scrollHeight');
        if (scrollHeight && jobs.items.length > 0) {
            window.scrollTo(0, Number.parseInt(scrollHeight, 10));
            sessionStorage.removeItem('scrollHeight');
        }
    }, [jobs.items]);
    useEffect(() => {
        const qsData = transformQueryTypes(mapData, rawParseQuery(search.replace(/\?/, '')));
        if (!isQueriesSame(qsData, filter)) {
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            qsData.page ||= 1;
            updateFilter(qsData);
        }
    }, [windowPathname, search]);
    useEffect(() => {
        if (!state.accounts && !!state.user) {
            asyncDispatch(loadAccounts()).catch(console.error);
        }
    }, [!state.accounts && !!state.user]);
    useEffect(() => {
        const rawQuery = rawParseQuery();
        const parsed = transformQueryTypes(mapData, rawQuery);
        const hasExplicitAccountId = 'accountId' in rawQuery;
        updateFilter({
            accountId: isAdmin && !hasExplicitAccountId && !rawQuery['ids']
                ? (state.account?.sid ?? '')
                : (parsed.accountId ?? ''),
            ...parsed
        });
    }, [state.account, isAdmin]);
    useEffect(() => {
        const { isInitial } = pushFiltersSearchPath({
            filter: debouncedFilter,
            initialData: initialData,
            mapData,
            path: basePath
        });
        if (!isOpenFilters && !isInitial) {
            setIsOpenFilters(true);
        }
        setLoading(true);
        const { tableView, ...applicableFilters } = debouncedFilter;
        resetIapAuthRequired();
        asyncDispatch(loadJobs({
            ...applicableFilters,
            ids: debouncedFilter.ids ? [debouncedFilter.ids] : '',
            withPhotoUrls: tableView !== 1
        }, isAdmin))
            .then(setJobs, console.error)
            .finally(() => {
            setLoading(false);
        });
    }, [debouncedFilter]);
    const updateFilter = (newFilter) => {
        setFilter({ ...initialData, ...newFilter });
    };
    const handleChangeFilter = (fieldName, value) => {
        updateFilter({
            ...filter,
            [fieldName]: value,
            page: 1
        });
    };
    const handleResetFilter = () => {
        updateFilter(initialData);
    };
    const handlePageChange = (_event, page) => {
        updateFilter({
            ...filter,
            page
        });
    };
    return (React.createElement("div", { className: "Jobs" },
        isAdmin && isIapAuthRequired() && (React.createElement(Container, null,
            "This page is protected by IAP - ",
            React.createElement(AuthWithGoogleButton, null))),
        React.createElement("div", { className: "jobs-header g-page-name" },
            React.createElement("h1", { className: "jobs-header__title" },
                isAdmin ? 'All' : '',
                " Jobs")),
        React.createElement(JobsFilter, { filterFields: filter, isAdmin: isAdmin, jobsOptions: jobsOptions, onFilter: handleChangeFilter, onReset: handleResetFilter }),
        filter.tableView === 1 && (React.createElement(List, { columns: jobsColumnsProps, "data-test": "jobs-list", handlePageChange: handlePageChange, isClickable: (item) => {
                const NOT_CLICKABLE = ['removed'];
                return !NOT_CLICKABLE.includes(item.status.toLowerCase());
            }, items: jobs.items, label: "jobs", loading: loading, onClick: (item) => {
                sessionStorage.setItem('scrollHeight', window.scrollY.toString());
                if (isAdmin) {
                    // Preserve accountId in URL so back-navigation does not re-auto-populate it
                    const params = new URLSearchParams(window.location.search);
                    if (!params.has('accountId')) {
                        params.set('accountId', filter.accountId || '');
                        window.history.replaceState(window.history.state, '', `${basePath}?${params.toString()}`);
                    }
                }
                navigate(`/${isAdmin ? 'admin' : 'account'}/jobs/${item.id}`);
            }, page: filter.page, total: jobs.total, totalPages: jobs.totalPages }))));
};
